import { customerActionTypes as actionTypes } from '../ActionTypes/customerActionTypes'
import { commonActionTypes } from '../ActionTypes/commonActionTypes'
import commonActions from './commonActions'
import compensationActions from './compensationActions'
import searchActions from './searchActions'

import helpers from '../Utils/helpers'
import { httpFetch } from '../Utils/httpUtils'
import * as formValidationRules from '../Constants/formValidationRules'
import leadActions from './leadActions';
import orderTypeMap from '../Constants/orderTypeMap';
import actionLogActions from './actionLogActions';
import sorting from "../Utils/sorting";
import forms from '../Constants/forms'
import formActions from "./formActions";
import { TerminationConfigs, TerminationTypes } from '../Constants/VOSS/Termination/TerminationConfigs'

const customerActions = {

    showCustomer(json) {
        return {
            type: actionTypes.SHOW_CUSTOMER,
            customer: json
        }
    },

    reset() {
        return {
            type: actionTypes.RESET
        }
    },

    showVonAccounts(json) {
        return {
            type: actionTypes.SHOW_VON_ACCOUNTS,
            data: json
        }
    },

    isCustomerValid(isValid) {
        return {
            type: actionTypes.CUSTOMER_DATA_STATUS,
            isValid: isValid
        }
    },
    getSelectedCustomer(customerId, callback) {
        return (dispatch) => {
            dispatch(customerActions.reset());
            dispatch(customerActions.getSelectedCustomerFromApi(customerId, callback));
        }
    },

    /// Low level action method. Use getCustomer(customerNumber) instead.
    getSelectedCustomerFromApi(customerId, callback) {
        return dispatch => {
            dispatch(commonActions.progressLoader(true))

            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "selectedCustomer", value: true });
            return httpFetch(`/api/customer/${customerId}`, dispatch)
                .then(json => {
                    if (json) {
                        const result = customerActions.validateCustomer(json);
                        dispatch(customerActions.isCustomerValid(result === null));
                        dispatch(customerActions.showCustomer(json))
                        dispatch(commonActions.showVonWarning(!json?.oldVbCustomer?.vonResponse && !json?.vossCustomer))
                        if (callback)
                            callback(json.pathId);
                    }
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "selectedCustomer", value: false });
                });
        }
    },

    getSelectedCustomerForEdit(customerId, validationRules) {
        return dispatch => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "editCustomer", value: true });
            return httpFetch(`/api/customer/${customerId}/editform`, dispatch)
                .then(json => {
                    dispatch(formActions.startFormEditing(forms.customerFormData, { ...json }, validationRules ?? formValidationRules.VossCustomerForm));
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "editCustomer", value: false });
                });
        }
    },

    createCustomer(customerForm, callback) {
        return dispatch => {

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(customerForm.data)
            }

            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "saveCustomer", value: true });
            return httpFetch('/api/customer/save', dispatch, httpRequest)
                .then(json => {
                    if (json) {
                        dispatch(searchActions.emptySearchResult());
                        dispatch(searchActions.search(json));
                        dispatch(commonActions.cancelFormEditing(customerForm.id));

                        if (callback)
                            callback();

                        dispatch(commonActions.showSuccess('Customer successfully created but it might take a while for all data to be visible.'));
                    }
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "saveCustomer", value: false });
                });

        }
    },

    saveCustomer(customerForm, callback) {
        return dispatch => {
            const httpRequest = {
                method: 'PUT',
                body: JSON.stringify(customerForm.data)
            }

            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "saveCustomer", value: true });
            return httpFetch('/api/customer/save', dispatch, httpRequest)
                .then(json => {
                    if (json) {
                        dispatch(customerActions.getSelectedCustomer(json));
                        dispatch(commonActions.cancelFormEditing(customerForm.id));

                        if (callback)
                            callback();

                        dispatch(commonActions.showSuccess('Customer updated successfully'));
                    }
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "saveCustomer", value: false });
                });
        }
    },

    beginVossCustomerCreation(formId, searchInput, leadId) {
        return dispatch => {
            let data = { organizationNumber: searchInput };

            if (leadId) {
                data.leadId = leadId;
            }

            dispatch(formActions.startFormEditing(formId, data, formValidationRules.CustomerSearchForm));
        }
    },

    createTestCustomer(formId, callback) {
        return dispatch => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "customerFromBusinessCheck", value: true });
            return httpFetch(`/api/customer/testcustomer`, dispatch).then(json => {
                dispatch(formActions.startFormEditing(formId, json, formValidationRules.VossCustomerForm));

                if (callback)
                    callback();
            }).finally(() => {
                dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "customerFromBusinessCheck", value: false });
            })
        }
    },

    createVossCustomerFromBusinessCheck(customerForm, formId, callback) {
        return dispatch => {
            customerForm.validationRules = formValidationRules.CustomerSearchForm;
            const validationResult = helpers.validateForm(customerForm);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            this.getVossCustomerFromBusinessCheck(customerForm.data.organizationNumber, false).then(json => {
                if (json) {
                    if (customerForm.data.leadId) {
                        json.leadId = customerForm.data.leadId
                    }

                    dispatch(formActions.startFormEditing(formId, json.customer, formValidationRules.VossCustomerForm));
                    if (!json.success)
                        dispatch(commonActions.showWarning('No customer data was found for the provided organization number. Please fill in the information manually.'));
                }

                if (callback)
                    callback();
            });
        }
    },

    updateVossCustomerFromBusinessCheck(organizationNumber, form) {
        return dispatch => {
            return this.getVossCustomerFromBusinessCheck(organizationNumber, true).then(json => {
                if (json.success) {
                    let customer = json.customer;
                    dispatch(formActions.changeMultiFormField(form, 'careOf', customer.careOf ?? form.data?.careOf));
                    dispatch(formActions.changeMultiFormField(form, 'postalCode', customer.postalCode ?? form.data?.postalCode));
                    dispatch(formActions.changeMultiFormField(form, 'city', customer.city ?? form.data?.city));
                    dispatch(formActions.changeMultiFormField(form, 'address', customer.address ?? form.data?.address));
                } else {
                    dispatch(commonActions.showWarning('No address info was found for the provided organization number.'));
                }
            })
        }
    },

    getVossCustomerFromBusinessCheck(organizationNumber, ignore = false) {
        return dispatch => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "customerFromBusinessCheck", value: true });
            return httpFetch(`/api/customer/${organizationNumber}/businesscheckvoss?ignoreExistingCustomerValidation=${ignore}`, dispatch).then(json => {
                return json;
            }).finally(() => {
                dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "customerFromBusinessCheck", value: false });
            })
        }
    },

    getCustomerByOrganizationNumber(customerForm, formId, callback) {
        return dispatch => {

            const validationResult = helpers.validateForm(customerForm);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch(`/api/customer/${customerForm.data.organizationNumber}/businesscheck`, dispatch)
                .then(json => {
                    if (customerForm.data.leadId) {
                        json.leadId = customerForm.data.leadId
                    }

                    dispatch(commonActions.startFormEditing(formId, json, formValidationRules.CustomerForm));
                    callback();
                })
        }
    },

    getCustomerVonProducts(customerId) {
        return dispatch => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "vonProducts", value: true });
            return httpFetch(`/api/customer/${customerId}/vonproducts`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_VONPRODUCTS, data: json });
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "vonProducts", value: false });
                })
        }
    },

    getCustomerVBProducts(customerNumber) {
        return dispatch => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "vbProducts", value: true });
            return httpFetch(`/api/customer/${customerNumber}/vbproducts`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_VBPRODUCTS, data: json });
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "vbProducts", value: false });
                })
        }
    },

    getCustomerInfoForUpdate(organizationNumber) {
        return dispatch => {

            dispatch(commonActions.progressLoader(true))

            return httpFetch(`/api/customer/${organizationNumber}/businesscheckinfo`, dispatch)
                .then(json => {

                    dispatch(commonActions.progressLoader(false))
                    dispatch(commonActions.startFormEditing("updateCustomerForm", json, formValidationRules.CustomerAddressForm));
                })
        }
    },

    startNewDiscountModal(data) {
        return (dispatch) => {
            return httpFetch(`/api/discount/getAllVonDiscounts/`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_VON_DISCOUNTS, data: json });
                    dispatch(commonActions.startFormEditing("newDiscountModal", data, formValidationRules.NewDiscountForm))
                })
        }
    },

    startEducationBooking(data, courseId) {
        return dispatch => {
            dispatch(customerActions.getEducation(courseId));
            dispatch(commonActions.startFormEditing("bookEducation", data, formValidationRules.EducationForm));
            dispatch(leadActions.getMyLeadsForCustomer(data.customerNumber));
        }
    },

    sendEducationBooking(booking, callback) {
        return dispatch => {
            dispatch(commonActions.hideAlert());

            const validationResult = helpers.validateForm(booking);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(booking.data)
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch('/api/education/book', dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.cancelFormEditing(booking.id))
                    callback();
                    dispatch(commonActions.showSuccess('Booking sent successfully'));
                })
        }
    },

    showVonAccountForm(formData, customer) {
        return dispatch => {

            dispatch(commonActions.hideAlert());

            const validationResults = customerActions.validateCustomer(customer);

            if (validationResults !== null) {
                dispatch(commonActions.showWarning('Please update customer form before creating VON account!'));
                return null;
            }

            dispatch(commonActions.startFormEditing("createVonAccount", formData, formValidationRules.CreateVonAccount));
        }
    },

    validateCustomer(customer) {
        const input = { validationRules: formValidationRules.CustomerForm, data: customer };

        const validationResults = helpers.validateForm(input);

        return validationResults;
    },

    saveVonAccount(vonAccountForm, callback) {
        return dispatch => {
            dispatch(commonActions.hideAlert());

            const validationResult = helpers.validateForm(vonAccountForm);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(vonAccountForm.data)
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch('/api/customer/createVonAccount', dispatch, httpRequest)
                .then(json => {
                    dispatch(customerActions.getSelectedCustomer(vonAccountForm.data.customerNumber));
                    dispatch(commonActions.showSuccess('VON account has been created'));
                    dispatch(commonActions.cancelFormEditing("createVonAccount"));
                    callback();
                })
        }
    },

    getVonAccounts(customerId) {
        return (dispatch, getState) => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "vonAccounts", value: true });
            return httpFetch(`/api/customer/${customerId}/vonaccounts`, dispatch)
                .then(json => {
                    dispatch(customerActions.showVonAccounts(json));
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "vonAccounts", value: false });
                })
        }
    },


    beginVonLicenseAdministrationForm(vonAccounts, service) {
        return dispatch => {
            const mappedVonAccounts = vonAccounts.map(account => {
                const role = account.roles.find(role => {
                    return service.vonServiceRoles.some(serviceRole => serviceRole.name === role.serviceRole);
                });

                const roleId = role
                    ? service.vonServiceRoles.find(serviceRole => serviceRole.name === role.serviceRole).id
                    : "0";

                return {
                    id: account.id,
                    name: account.name,
                    email: account.email,
                    roleId: roleId,
                    employeeType: account.employeeType,
                };
            });

            let data = {
                service: service,
                vonAccounts: mappedVonAccounts
            };

            dispatch(commonActions.startFormEditing('vonLicenseAdministrationForm', data));
        };
    },


    setSearchVonAccountInput(input) {
        return {
            type: actionTypes.SET_VON_ACCOUNT_SEARCH_INPUT,
            vonAccountInput: input
        }
    },

    updateCustomerAppointmentsCount(number) {
        return {
            type: actionTypes.UPDATE_CUSTOMER_APPOINTMENTS_COUNT,
            openAppointments: number
        }
    },

    updateCustomerLeadsCount(number) {
        return {
            type: actionTypes.UPDATE_CUSTOMER_LEADS_COUNT,
            openLeadsCount: number
        }
    },

    updateCustomerAOLeadsCount(number) {
        return {
            type: actionTypes.UPDATE_CUSTOMER_AOLEADS_COUNT,
            openAOLeadsCount: number
        }
    },

    selectAdminForm(id) {
        return {
            type: actionTypes.SELECT_ADMIN_FORM,
            selectedAdminForm: id
        }
    },

    getAdminFormTemplate(customerNumber, customerId, id) {
        return dispatch => {
            dispatch(commonActions.cancelFormEditing("adminForm"))
            dispatch(commonActions.progressLoader(true));

            let params = "?";
            if (customerNumber)
                params += `customerNumber=${customerNumber}&`;

            if (customerId)
                params += `customerId=${customerId}&`;

            return httpFetch(`/api/adminForms/getTemplate/${id}${params}`, dispatch)
                .then(json => {
                    dispatch(commonActions.startFormEditing('adminForm', json, formValidationRules.EmailTemplate));
                })
        }
    },

    getTemplateApprovalTemplate(templateId) {
        return dispatch => {
            dispatch(commonActions.cancelFormEditing("templateApprovalForm"))
            return httpFetch(`/api/adminForms/getTemplateApprovalTemplate/${templateId}`, dispatch)
                .then(json => {
                    dispatch(commonActions.startFormEditing('templateApprovalForm', json, formValidationRules.EmailTemplate));
                })
        }
    },

    getFormFromEmailActionLog(emailActionLogId) {
        return dispatch => {
            dispatch(commonActions.cancelFormEditing("adminForm"))
            dispatch(actionLogActions.getFormFromEmailActionLog(emailActionLogId, json =>
                dispatch(commonActions.startFormEditing('adminForm', json, formValidationRules.EmailTemplate))))
        }
    },

    sendAdminForm(adminForm, callback, successMessage) {
        return dispatch => {
            dispatch(commonActions.hideAlert());

            const validationResult = helpers.validateForm(adminForm);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(adminForm.data)
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch('/api/adminForms/send', dispatch, httpRequest)
                .then(json => {

                    dispatch(commonActions.showSuccess(`${successMessage ? successMessage : 'Email has been sent'}`));
                    dispatch(commonActions.cancelFormEditing(adminForm.id));
                    dispatch(customerActions.selectAdminForm(null));
                    if (callback) {
                        callback();
                    }

                })
        }
    },

    getCustomerInvoices(customerNumber) {
        return (dispatch) => {
            return httpFetch(`/api/invoice/GetCustomerInvoices/${customerNumber}`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_INVOICES, data: json });
                })
        }
    },

    getInvoiceEmailTemplate(customerNumber, solumInvoiceOCR, vbInvoiceOCR, invoiceNumber, id) {
        return (dispatch) => {
            return httpFetch(`/api/invoice/GetTemplate/${customerNumber}/${solumInvoiceOCR}/${vbInvoiceOCR}/${invoiceNumber}/${id}`, dispatch)
                .then(json => {
                    dispatch(commonActions.startFormEditing('invoiceEmail', json, formValidationRules.EmailTemplate));
                    dispatch(commonActions.showModal(true));
                })
        }
    },

    showCancelVbAgreementModal(product, customer) {
        return (dispatch, getState) => {
            return customerActions.getVbTerminationReasons(dispatch, getState().CommonReducer.dropdownlists['VbTerminationReasons'])
                .then(() => {
                    dispatch(customerActions.getTerminationEmailTemplate(customer.vbCustomer.customerNumber, product.productNumber))
                        .then(json => {
                            dispatch(commonActions.startFormEditing('cancelVbAgreement', { ...{ ...json, to: customer.contactPerson?.email ?? customer.email }, product, reference: '', reasonCode: '', formName: 'CancelVbAgreement' }, formValidationRules.CancelAgreement));
                        })
                });
        }
    },

    getVbTerminationReasons(dispatch, list) {
        if (!list) {
            return httpFetch(`/api/termination/GetVbTerminationReasons`, dispatch)
                .then(json => {
                    json = [{ id: "", name: "" }, ...json]

                    const addEmptyChoice = item => {
                        if (item.secondLevelReasons) {
                            item.secondLevelReasons.forEach(item2 => addEmptyChoice(item2))
                            item.secondLevelReasons = [{ id: "", name: "" }, ...item.secondLevelReasons]
                        }
                        if (item.churnEffectReasons && item.churnEffectReasons.length > 0)
                            item.churnEffectReasons = [{ id: "", name: "" }, ...item.churnEffectReasons]
                    }
                    json.forEach(reason => addEmptyChoice(reason))
                    dispatch(commonActions.storeDropDownList('VbTerminationReasons', json));
                })
        } else {
            return Promise.resolve('success');
        }
    },

    getTerminationEmailTemplate(customerNumber, productNumber) {
        return (dispatch) => {
            return httpFetch(`/api/termination/GetTerminationEmailTemplate/${customerNumber}/${productNumber}`, dispatch);
        }
    },

    changeTerminationEmailTemplate(dispatch, customerNumber, form, cancellationDate) {
        var apiMethod = cancellationDate === form.data.product.purchaseDate ? `GetTerminationReturnEmailTemplate` : `GetTerminationEmailTemplate`;
        return httpFetch(`/api/termination/` + apiMethod + `/${customerNumber}/${form.data.product.productNumber}`, dispatch).then(json => {
            dispatch(commonActions.startFormEditing(form.id, { ...{ ...json, to: form.data.to }, product: form.data.product, reference: form.data.reference, reasonCode: form.data.reasonCode, formName: form.name, cancellationDate }, formValidationRules.CancelAgreement));
        })
    },

    cancelVbAgreement(customerNumber, form, callback) {
        return dispatch => {
            dispatch(commonActions.hideAlert());

            const validationResult = helpers.validateForm(form);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            const data = form.data;

            var payload = {
                reasonCode: data.reasonCode,
                secondLevelReasonCode: data.reasonCode2,
                churnEffectId: data.churnEffect,
                churnEffectExtraInfo: data.churnEffectExtraInfo,
                secondLevelReason: data.secondLevelReason,
                comment: data.comment,
                reference: data.reference,
                customerNumber,
                productNumber: data.product.productNumber,
                agreementTerminationDate: data.cancellationDate,
                agreementPurchaseDate: data.product.purchaseDate,
                terminationEmail: { body: data.body, to: data.to, from: data.from, subject: data.subject, templateType: data.templateType }
            };

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(payload)
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch('/api/termination/cancelVbAgreement', dispatch, httpRequest)
                .then(json => {
                    dispatch(customerActions.getSelectedCustomer(customerNumber));
                    if (payload.agreementTerminationDate !== payload.agreementPurchaseDate) {
                        dispatch(commonActions.showSuccess('Agreement has been cancelled'));
                    }
                    else {
                        dispatch(commonActions.showSuccess('Request has been sent'));
                    }
                    callback();
                })
                .catch(error => {
                    callback();
                });
        }
    },

    sendInvoiceEmail(invoiceForm) {
        return dispatch => {
            dispatch(commonActions.hideAlert());

            const validationResult = helpers.validateForm(invoiceForm);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(invoiceForm.data)
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch(`/api/invoice/send`, dispatch, httpRequest)
                .then(json => {

                    dispatch(customerActions.getCustomerInvoices(invoiceForm.data.customerNumber))
                    dispatch(commonActions.showSuccess('Invoice has been sent'));
                    dispatch(commonActions.cancelFormEditing("invoiceEmail"));
                    dispatch(commonActions.showModal(false));
                })
        }
    },

    showInvoiceCreditModal(customer, invoice) {
        return (dispatch, getState) => {
            return customerActions.getInvoiceCreditReasons(dispatch, getState().CommonReducer.dropdownlists['InvoiceCreditReasons'])
                .then(() => {
                    return dispatch(customerActions.getInvoiceCreditEmailTemplate(customer.vbCustomer.customerNumber, invoice.invoiceNumber))
                        .then(json => {
                            const form = {
                                ...{ ...json }, customerNumber: customer.vbCustomer.customerNumber, customerName: customer.name,
                                customerPhone: customer.vbCustomer.phoneNumber, invoiceNumber: invoice.invoiceNumber, partialCredit: false, partialCreditReadAble: 'No', invoiceCreditReason: 0, invoiceCreditReason2: 0, invoiceCreditReasonName: 0, invoiceCreditReason2Name: 0, invoiceCreditComment: '', sum: invoice.amount, customerEmail: customer.email, contactPerson: customer.contactPerson?.fullName, formName: 'sendInvoiceCreditForm'
                            }

                            dispatch(commonActions.startFormEditing('sendInvoiceCreditForm', form, formValidationRules.InvoiceCreditForm));
                        })
                });
        };
    },

    getInvoiceCreditReasons(dispatch, list) {
        if (!list) {
            return httpFetch(`/api/invoice/GetInvoiceCreditReasons`, dispatch)
                .then(json => {
                    json = [{ id: 0, name: "" }, ...json]

                    const addEmptyChoice = item => {
                        if (item.secondLevelReasons) {
                            item.secondLevelReasons.forEach(item2 => addEmptyChoice(item2))
                            item.secondLevelReasons = [{ id: 0, name: "" }, ...item.secondLevelReasons]
                        }
                    }
                    json.forEach(reason => addEmptyChoice(reason))
                    dispatch(commonActions.storeDropDownList('InvoiceCreditReasons', json));
                })
        } else {
            return Promise.resolve('success');
        }
    },

    switchInvoiceCreditValidation(partialCredit, secondReason, form) {
        return dispatch => {

            form.data.body = form.data.body_copy
            if (partialCredit && secondReason)
                dispatch(commonActions.startFormEditing(form.id, form.data, formValidationRules.InvoiceCreditFormPartialCreditWithSecondReason))
            else if (partialCredit && !secondReason)
                dispatch(commonActions.startFormEditing(form.id, form.data, formValidationRules.InvoiceCreditFormPartialCredit))
            else if (!partialCredit && secondReason)
                dispatch(commonActions.startFormEditing(form.id, form.data, formValidationRules.InvoiceCreditFormWithSecondReason))
            else
                dispatch(commonActions.startFormEditing(form.id, form.data, formValidationRules.InvoiceCreditForm))

            dispatch(commonActions.changeMultiFormField(form.id, 'partialCredit', partialCredit));
        }
    },

    getInvoiceCreditEmailTemplate(customerNumber, invoiceNumber) {
        return (dispatch) => {
            return httpFetch(`/api/invoice/getInvoiceCreditEmailTemplate/${customerNumber}/${invoiceNumber}`, dispatch)
        }
    },

    sendInvoiceCredit(customerNumber, form, body, callback) {
        return (dispatch) => {
            dispatch(commonActions.hideAlert());

            const validationResult = helpers.validateForm(form);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            const data = form.data;

            var payload = {
                InvoiceCredit: {
                    invoiceCreditReason: data.invoiceCreditReason,
                    invoiceCreditReason2: data.invoiceCreditReason2 == "" ? 0 : data.invoiceCreditReason2,
                    partialCredit: data.partialCredit,
                    comment: data.invoiceCreditComment,
                    invoiceNumber: data.invoiceNumber,
                    sum: parseFloat(data.sum.toString().replace(',', '.')),
                    customerNumber: customerNumber
                },
                InvoiceCreditEmail: { body: body, to: data.to, from: data.from, subject: data.subject, templateType: data.templateType, templateId: data.templateId, customerNumber: customerNumber }
            };

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(payload)

            }

            dispatch(commonActions.progressLoader(true))
            return httpFetch('/api/invoice/sendinvoicecredit', dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.showSuccess('Request has been sent'));
                    callback();
                });
        }
    },

    activateTransactionService(vonServiceId, customerNumber) {
        return (dispatch) => {

            const httpRequest = {
                method: 'PUT',
                body: JSON.stringify(vonServiceId)
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch(`/api/transactionServices/activate/${customerNumber}`, dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.showSuccess('Transaction Service Activated'));
                    dispatch(customerActions.getSelectedCustomer(customerNumber));
                })
        }
    },

    getVceEmailTemplate(organizationNumber, customerName) {

        return (dispatch) => {
            return httpFetch(`/api/transactionServices/VceTemplate/${organizationNumber}/${customerName}`, dispatch)
                .then(json => {
                    dispatch(commonActions.startFormEditing("vceEmailTemplate", json, formValidationRules.VceTemplate));
                })
        }
    },

    activateVce(vceEmailTemplate, customerNumber, callback) {

        return dispatch => {
            dispatch(commonActions.hideAlert());

            const validationResult = helpers.validateForm(vceEmailTemplate);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(vceEmailTemplate.data)
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch(`/api/transactionServices/ActivateVce/${customerNumber}`, dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.showSuccess('VCE has been activated'));
                    dispatch(customerActions.getSelectedCustomer(customerNumber));

                    callback();
                })
        }
    },

    updateCustomerAddress(addressForm, customerNumber) {
        return dispatch => {
            dispatch(commonActions.hideAlert());

            const validationResult = helpers.validateForm(addressForm);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(addressForm.data)
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch(`/api/customer/${customerNumber}/UpdateAddress`, dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.showSuccess('Customer address updated, it might take a while for the new address to appear in the system'));
                    dispatch(commonActions.cancelFormEditing("updateCustomerForm"));
                })
        }
    },

    getCustomerAttendedEducations(customerNumber) {
        return (dispatch) => {
            return httpFetch(`/api/education/GetAttendedEducations/${customerNumber}`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_ATTENDED_EDUCATIONS, data: json });
                })
        }
    },

    getCustomerAttendedOnboardings(customerNumber) {
        return (dispatch) => {
            return httpFetch(`/api/bokamera/GetAttendedOnboardings/${customerNumber}`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_ATTENDED_ONBOARDINGS, data: json });
                })
        }
    },

    getOnboardingFromId(onboardingId) {
        return (dispatch) => {
            return httpFetch(`/api/bokamera/GetOnboardingById/${onboardingId}`, dispatch)
                .then(json => {
                    dispatch(commonActions.startFormEditing("attendedOnboardingForm", json, null));
                })
        }
    },

    getEducations() {
        return (dispatch) => {
            return httpFetch(`/api/education/GetEducations`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_EDUCATIONS, data: json });
                })
        }
    },

    getEducation(id) {
        return (dispatch) => {
            return httpFetch(`/api/education/GetEducation/${id}`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_EDUCATION, data: json });
                })
        }
    },

    getCourse(id) {
        return (dispatch) => {
            return httpFetch(`/api/education/GetCourse/${id}`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_COURSE, data: json });
                })
        }
    },

    clearSelectedCourse() {
        return (dispatch) => {
            dispatch({ type: actionTypes.STORE_COURSE, data: null });
        }
    },

    getBokaMeraServices() {
        return (dispatch) => {
            return httpFetch(`/api/bokamera/GetServices`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_BOKAMERA_SERVICES, data: json });
                })
        }
    },

    getBokaMeraTimes(id, weekFromNow) {
        return (dispatch) => {

            dispatch(commonActions.progressLoader(true))

            return httpFetch(`/api/bokamera/GetAvailableTimes/${id}/${weekFromNow}`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_BOKAMERA_TIMES, data: json, bokaMeraServiceId: id, bokaMeraWeekNo: weekFromNow });
                })
        }
    },

    clearBokaMeraTimes() {
        return (dispatch) => {
            dispatch({ type: actionTypes.CLEAR_BOKAMERA_TIMES, data: null, bokaMeraServiceId: null, bokaMeraWeekNo: null });
        }
    },

    getBokaMeraCustomFields() {
        return (dispatch) => {
            return httpFetch(`/api/bokamera/Getcustomfields`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_BOKAMERA_CUSTOMFIELDS, data: json });
                })
        }
    },

    setBokaMeraModalObject(fromDate, toDate, service, availableResources) {
        return (dispatch) => {
            dispatch({ type: actionTypes.SET_BOKA_MERA_MODAL_OBJECT, fromDate: fromDate, toDate: toDate, service: service, availableResources: availableResources })
        }
    },


    startBokaMeraModal(data) {
        return (dispatch) => {
            dispatch(commonActions.startFormEditing("bokaMera", data, formValidationRules.BokaMeraForm))
        }
    },

    saveBokaMeraBooking(bokaMeraForm, callback) {
        return (dispatch) => {
            dispatch(commonActions.hideAlert());

            const validationResult = helpers.validateForm(bokaMeraForm);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            bokaMeraForm.data.serviceName = bokaMeraForm.data.service.name;

            bokaMeraForm.data.customFields.forEach(field => {
                for (let copy of Object.keys(bokaMeraForm.data)) {
                    if (copy === field.name)
                        field.value = bokaMeraForm.data[copy];
                }
            })

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(bokaMeraForm.data)
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch(`/api/bokamera/createBooking`, dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.cancelFormEditing(bokaMeraForm.id));
                    dispatch(commonActions.showSuccess(json.successMessage));
                    callback();
                })
        }
    },

    clearCustomFields(customFields) {
        return (dispatch) => {
            dispatch({ type: actionTypes.CLEAR_CUSTOM_FIELDS, data: customFields });
        }
    },


    updateOnboardingDetails(onboardingForm, callback) {
        return (dispatch) => {
            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(onboardingForm.data)
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch(`/api/bokamera/updateOnboarding`, dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.cancelFormEditing("attendedOnboardingForm"));
                    dispatch(commonActions.showSuccess(json.successMessage));
                    callback();
                })
        }
    },


    createConsultingForm(consultingForm) {
        return dispatch => {
            dispatch(commonActions.startFormEditing('consulting', consultingForm, formValidationRules.ConsultingForm));
        }
    },

    getConsultingProduct(id) {
        return dispatch => {
            return httpFetch(`/api/consulting/GetProduct/${id}`)
                .then(json => {
                    dispatch(commonActions.changeMultiFormField('consulting', 'product', json));
                    dispatch(commonActions.changeMultiFormField('consulting', 'totalCost', json.price));
                    dispatch(commonActions.changeMultiFormField('consulting', 'price', json.price));
                })
        }
    },

    getConsultingTotalCost(productId, hours, price) {
        hours = helpers.formatComma(hours)
        return (dispatch) => {
            return httpFetch(`/api/consulting/GetTotalCost/${productId}/${hours}/${price}`, dispatch)
                .then(json => {
                    dispatch(commonActions.changeMultiFormField('consulting', 'totalCost', json));
                })
        }
    },

    saveConsulting(consultingForm, customerNumber, callback) {
        return dispatch => {
            dispatch(commonActions.hideAlert());

            const validationResult = helpers.validateForm(consultingForm);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            var payload = {
                customerNumber,
                salesforceTicketId: consultingForm.data.salesforceTicketId,
                hoursCharged: consultingForm.data.hoursCharged ? helpers.formatComma(consultingForm.data.hoursCharged) : consultingForm.data.hoursCharged,
                price: consultingForm.data.price,
                customerReference: consultingForm.data.reference,
                productId: consultingForm.data.productId,
                salesNumber: consultingForm.data.salesNumber,
                travelCost: consultingForm.data.travelCost,
                customerEmailAddress: consultingForm.data.attendeeEmail,
                discount: consultingForm.data.discount ? consultingForm.data.discount : 0,
                lengthInHours: consultingForm.data.lengthInHours,
                courseCoordinator: consultingForm.data.courseCoordinator,
                courseDate: consultingForm.data.courseDate
            };

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(payload)
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch('/api/consulting/save', dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.showSuccess('Consultation was saved!'));
                    // dispatch(commonActions.cancelFormEditing("consulting"));
                    callback();
                })
        }
    },

    getDesktopLicenses(customerNumber) {
        return (dispatch) => {
            return httpFetch(`/api/desktopversion/getDesktopVersions/${customerNumber}`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.GET_DESKTOP_LICENSES, data: json });
                })
        }
    },

    getSendDesktopLicenseTemplate(customerNumber, versionid, version, licenseKey) {
        return (dispatch) => {
            return httpFetch(`/api/desktopversion/getEmailTemplate/${customerNumber}/${versionid}/${version && version.toString()}/${licenseKey}`, dispatch)
                .then(json => {
                    dispatch(commonActions.startFormEditing('sendDesktopVersion', json, formValidationRules.EmailTemplate));
                    dispatch(commonActions.showModal(true));
                })
        }
    },

    sendDesktopVersionEmail(emailForm, callback) {
        return dispatch => {
            dispatch(commonActions.hideAlert());

            const validationResult = helpers.validateForm(emailForm);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(emailForm.data)
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch('/api/desktopversion/send', dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.showModal(false));
                    dispatch(commonActions.showSuccess('Desktop version has been sent'));
                    dispatch(commonActions.cancelFormEditing("sendDesktopVersion"));
                    if (callback)
                        callback();
                })
        }
    },

    getCollaborations(customerId, isAllowedToInvite, abortController) {
        return (dispatch) => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "collaborations", value: true });
            return httpFetch(`/api/customer/${customerId}/collaborations?isAllowedToInvite=${isAllowedToInvite}`, dispatch, { signal: abortController.signal })
                .then(json => {
                    dispatch({ type: actionTypes.STORE_COLLABORATIONS, data: json });
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "collaborations", value: false });
                });
        }
    },

    deleteCollaborationTermination(collaborationId, callback) {
        return dispatch => {
            return httpFetch(`/api/customer/collaboration/${collaborationId}/deletetermination`, dispatch)
                .then(() => {
                    dispatch(commonActions.showSuccess('Collaboration has been restored'));
                    callback();
                })
        }
    },

    clearCollaborations() {
        return (dispatch) => {
            dispatch({ type: actionTypes.CLEAR_COLLABORATIONS, data: null });
        }
    },

    openCollaborationTermination(collaboration) {
        return dispatch => {
            return httpFetch(`/api/customer/collaboration/${collaboration.id}/termination`)
                .then(json => {
                    const data = {
                        collaboration: collaboration,
                        termination: json,
                        option: json.availableTerminationOptions[0].type,
                        endDate: helpers.getCurrentDate()
                    }
                    dispatch(commonActions.startFormEditing("collaborationTermination", data, null))
                })

        }
    },

    sendCollaborationTermination(collaborationId, form, callback) {
        return dispatch => {
            const data = form.data;
            const payload = {
                type: data.option,
                endDate: data.endDate,
                employeeId: data.termination.employeeId,
                reason: data.reason ? {
                    id: data.reason,
                    childReasonId: data.subReason,
                    text: data.comment
                } : null
            }

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(payload)
            }
            return httpFetch(`/api/customer/terminateCollaboration/${collaborationId}`, dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.showSuccess('Collaboration has been terminated'));
                    callback();
                })
        }
    },

    getAoCustomers(salesNumbers) {
        return (dispatch, getState) => {

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(salesNumbers)
            }

            return httpFetch(`/api/customer/aoCustomerForResponsiblePersons`, dispatch, httpRequest)
                .then(json => {
                    const list = sorting.sortListFromStorage("aoCustomers", json);
                    dispatch({ type: actionTypes.STORE_AO_CUSTOMERS, data: list });
                })
        }
    },

    clearAoCustomers() {
        return (dispatch, getState) => {
            dispatch({ type: actionTypes.STORE_AO_CUSTOMERS, data: null });
        }
    },

    getDiscounts(customerId) {
        return (dispatch) => {
            return httpFetch(`/api/customer/${customerId}/discounts`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_CUSTOMER_DISCOUNTS, data: json });
                })
        }
    },

    assignNewDiscount(form, customerId, callback) {
        return (dispatch) => {

            const validationResult = helpers.validateForm(form);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            var selectedDate = new Date(form.data.validToDate)
            var lastDayOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate();

            if (selectedDate.getDate() != lastDayOfMonth) {
                dispatch(commonActions.showWarning('Valid date should be the last day of month', 'Please validate!'));
                return null;
            }

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(form.data)
            }

            return httpFetch(`/api/discount/assignNewDiscount/${customerId}`, dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.cancelFormEditing("newDiscountModal"));
                    this.getDiscounts(customerId);
                    callback();
                })
        }
    },

    deleteDiscount(customerId, discountId) {
        return dispatch => {
            const httpRequest = {
                method: 'DELETE'
            }
            return httpFetch(`/api/discount/deleteCustomerDiscount/${customerId}/${discountId}`, dispatch, httpRequest).then(() => {
                dispatch(customerActions.getDiscounts(customerId))
            })
        }
    },

    getVossTerminationEmailTemplate(subscription) {
        return (dispatch, getState) => {

            return customerActions.getSubscriptionTerminationReasons(dispatch, getState().CommonReducer.dropdownlists['SubscriptionTerminationReasons'])
                .then(() => {
                    httpFetch(`/api/termination/GetVossTerminationEmailTemplate/${subscription.id}`, dispatch)
                        .then(json => {
                            dispatch(formActions.startFormEditing(forms.cancelVossSubscription,
                                {
                                    ...json,
                                    ...TerminationConfigs[TerminationTypes.CurrentPeriodEnd],
                                    orderType: 'ordinary'
                                }, formValidationRules.CancelSubscription));
                        })
                })
        }
    },

    searchSubscriptions(customerId) {
        return dispatch => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "subscriptions", value: true });
            return httpFetch(`/api/customer/${customerId}/subscriptions`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_SUBSCRIPTIONS, data: json });
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "subscriptions", value: false });
                })
        }
    },

    getDetailedSubscriptions(customerId) {
        return dispatch => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "detailedSubscriptions", value: true });
            return httpFetch(`/api/customer/${customerId}/detailedsubscriptions`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_DETAILED_SUBSCRIPTIONS, data: json });
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "detailedSubscriptions", value: false });
                })
        }
    },

    getSubscriptionTerminationReasons(dispatch, list) {
        return httpFetch(`/api/termination/GetVossTerminationReasons`, dispatch)
            .then(json => {
                dispatch(commonActions.storeDropDownList('SubscriptionTerminationReasons', json));
            })
    },

    generateVossTerminationRequest(form) {
        const data = form.data;
        let payload = {
            customerNumber: data.customerNumber,
            timing: data.timing,
            periodIterationCount: data.periodIterationCount,
            invoiceLineTexts: data.invoiceLineTexts ? [data.invoiceLineTexts] : [],
            reason: {
                reasonId: data.reason,
                subreasonId: data.subReason,
                comment: data.comment
            },
            creditOptions: {
                behaviour: data.behaviour,
                creditKind: data.creditKind,
            },
            periodAnchor: data.periodAnchor,
            terminationEmail: { body: data.body, to: data.to, from: data.from, subject: data.subject, templateType: data.templateType },
            useWarranty: data.useWarranty,
            isDryRun: form.data.isDryRun,
            orderType: orderTypeMap[data.orderType] ?? 0,
            salesPersonEmail: data.salesPersonEmail
        };

        return {
            method: 'POST',
            body: JSON.stringify(payload)
        };
    },

    updateVossTerminationEmailTemplate(form, subscription, callback) {
        return dispatch => {
            let httpRequest = customerActions.generateVossTerminationRequest(form, subscription);

            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "terminateSubscription", value: true });

            return httpFetch(`/api/termination/GetUpdatedVossTerminationEmailTemplate/${subscription.id}`, dispatch, httpRequest)
                .then(json => {
                    dispatch(formActions.changeMultiFormField(form, 'body', json.body));
                    dispatch(formActions.changeMultiFormField(form, 'subject', json.subject));
                    dispatch(formActions.changeMultiFormField(form, 'templateType', json.templateType));
                    dispatch(formActions.changeMultiFormField(form, 'from', json.from));
                    dispatch(formActions.changeMultiFormField(form, 'to', json.to));
                    callback();
                })
                .catch(error => {
                    dispatch(commonActions.showError('Failed to update email template'));
                })
                .finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "terminateSubscription", value: false });
                });
        }
    },

    cancelSubscription(form, subscription, notifyUser, callback) {
        return dispatch => {

            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "terminateSubscription", value: true });
            let httpRequest = customerActions.generateVossTerminationRequest(form, subscription);

            return httpFetch(`/api/termination/CancelSubscription/${subscription.id}?notifyUser=${notifyUser}`, dispatch, httpRequest)
                .then(json => {
                    callback(json);
                    dispatch(commonActions.showSuccess('Request has been sent'));
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "terminateSubscription", value: false });
                })
        }
    },

    getChangeSaasPeriodTemplate(customerNumber, productNumber) {
        return dispatch => {
            return httpFetch(`/api/customer/${customerNumber}/periodtemplate/${productNumber}`, dispatch)
                .then(json => {
                    dispatch(commonActions.startFormEditing('changeSaasPeriod', json, null));
                })
        }
    },

    changeSaasPeriod(form, callback) {
        return dispatch => {
            dispatch(commonActions.hideAlert());

            const validationResult = helpers.validateForm(form);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(form.data)
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch('/api/customer/changeSaasPeriod', dispatch, httpRequest)
                .then(json => {
                    dispatch(customerActions.getSelectedCustomer(form.data.customerNumber));
                    dispatch(commonActions.showSuccess('Request has been sent'));
                    callback();
                })
                .catch(error => {
                    if (error === 'BusinessWarningException') {
                        callback();
                    }
                });
        }
    },

    getSaasTerminationEmailTemplate(customerNumber, product) {
        return (dispatch, getState) => {
            return customerActions.getSaasTerminationReasons(dispatch, getState().CommonReducer.dropdownlists['SaasTerminationReasons'])
                .then(() => {
                    httpFetch(`/api/termination/GetSaasTerminationEmailTemplate/${customerNumber}/${product.productNumber}`, dispatch)
                        .then(json => {
                            const reasons = getState().CommonReducer.dropdownlists['SaasTerminationReasons']
                            dispatch(commonActions.startFormEditing('CancelSaasAgreement', { ...json, product, reference: '', reasonCode: reasons[0].id }, formValidationRules.CancelAgreement));
                        })
                })
        }
    },

    changeSaasTerminationEmailTemplate(dispatch, customerNumber, form, cancellationDate) {
        var apiMethod = cancellationDate === form.data.product.purchaseDate ? `GetSaasTerminationReturnEmailTemplate` : `GetSaasTerminationEmailTemplate`;
        return httpFetch(`/api/termination/` + apiMethod + `/${customerNumber}/${form.data.product.productNumber}`, dispatch).then(json => {
            dispatch(commonActions.startFormEditing(form.id, { ...{ ...json, to: form.data.to }, product: form.data.product, reference: form.data.reference, reasonCode: form.data.reasonCode, formName: form.name, cancellationDate }, formValidationRules.CancelAgreement));
        })
    },

    getSaasTerminationReasons(dispatch, list) {
        if (!list) {
            return httpFetch(`/api/termination/GetSaasTerminationReasons`, dispatch)
                .then(json => {
                    json = [{ id: "", name: "" }, ...json]

                    const addEmptyChoice = item => {
                        if (item.secondLevelReasons) {
                            item.secondLevelReasons.forEach(item2 => addEmptyChoice(item2))
                            item.secondLevelReasons = [{ id: "", name: "" }, ...item.secondLevelReasons]
                        }
                        if (item.churnEffectReasons && item.churnEffectReasons.length > 0)
                            item.churnEffectReasons = [{ id: "", name: "" }, ...item.churnEffectReasons]
                    }
                    json.forEach(reason => addEmptyChoice(reason))
                    dispatch(commonActions.storeDropDownList('SaasTerminationReasons', json));
                })
        } else {
            return Promise.resolve('success');
        }
    },

    cancelSaasAgreement(form, callback) {
        return dispatch => {
            dispatch(commonActions.hideAlert());

            const validationResult = helpers.validateForm(form);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            const data = form.data;

            var payload = {
                reasonCode: data.reasonCode,
                secondLevelReasonCode: data.reasonCode2,
                churnEffectId: data.churnEffect,
                churnEffectExtraInfo: data.churnEffectExtraInfo,
                secondLevelReason: data.secondLevelReason,
                comment: data.comment,
                reference: data.reference,
                customerNumber: data.customerNumber,
                productNumber: data.product.productNumber,
                productDescription: data.product.product,
                agreementTerminationDate: data.cancellationDate,
                agreementPurchaseDate: data.product.purchaseDate,
                terminationEmail: data
            };

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(payload)
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch('/api/termination/cancelSaasAgreement', dispatch, httpRequest)
                .then(json => {
                    dispatch(customerActions.getSelectedCustomer(data.customerNumber));
                    dispatch(commonActions.showSuccess('Request has been sent'));
                    callback();
                })
                .catch(error => {
                    callback();
                });
        }
    },

    getPendingTerminationRequests(customerNumber) {
        return dispatch => {
            return httpFetch(`/api/termination/pendingTerminationRequests/${customerNumber}`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_PENDING_TERMINATION_REQUESTS, data: json });
                })
        }
    },

    getInternalLeadEmailTemplate(customerNumber, templateType) {

        return (dispatch) => {
            return httpFetch(`/api/customer/${customerNumber}/internalLeadEmailTemplate/${templateType}`, dispatch)
                .then(json => {
                    dispatch(commonActions.startFormEditing("internalLeadEmail", json, formValidationRules.EmailTemplate));
                })
        }
    },

    setInternalLeadsEmailTemplate(emailTemplate) {
        return (dispatch) => {
            dispatch({ type: actionTypes.SET_INTERNAL_LEAD_EMAIL_TEMPLATE, data: emailTemplate });
        }
    },

    sendInternalAoLeadEmail(emailForm, callback) {
        return dispatch => {
            dispatch(commonActions.hideAlert());

            const validationResult = helpers.validateForm(emailForm);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(emailForm.data)
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch(`/api/customer/SendInternalAoLeadEmail`, dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.showSuccess('Lead has been sent'));
                    callback();
                })
        }
    },

    sendExternalLeadEmail(emailForm, callback) {
        return dispatch => {

            let fields = Object.entries(emailForm.data.fields).reduce((acc, [key, field]) => {
                if (field.value && typeof field.value === 'string') {
                    acc[key] = field.value;
                }
                return acc;
            }, {})

            const validationResult = helpers.validateForm({
                ...emailForm,
                data: {
                    ...emailForm.data,
                    ...fields
                }
            });

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify({ ...emailForm.data, fields })
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch(`/api/customer/SendExternalLeadEmail`, dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.showSuccess('Lead has been sent'));
                    callback();
                })
        }
    },

    sendInternalLeadEmail(emailForm, customerNumber, callback) {
        return dispatch => {
            dispatch(commonActions.hideAlert());

            const validationResult = helpers.validateForm(emailForm);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(emailForm.data)
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch(`/api/customer/SendInternalLeadEmail/${customerNumber}`, dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.showSuccess('Lead has been sent'));

                    callback();
                    this.getSelectedCustomer(customerNumber);
                })
        }
    },

    getRelatedCustomers(customerNumber) {
        return (dispatch) => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "relatedCustomers", value: true });
            return httpFetch(`/api/customer/${customerNumber}/relatedcustomers`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_RELATED_CUSTOMERS, data: json });
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "relatedCustomers", value: false });
                })
        }
    },

    setSelectedWebinar(webinar) {
        return dispatch => {
            dispatch({ type: actionTypes.SET_SELECTED_WEBINAR, data: webinar })
        }
    },

    getWebinars() {
        return (dispatch) => {
            return httpFetch(`/api/customer/webinars`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.GET_WEBINARS_FOR_SALES_PERSON, data: json });
                })
        }
    },

    getWebinarEmailTemplate(customerNumber, webinarId) {
        return (dispatch) => {
            return httpFetch(`/api/customer/${customerNumber}/webinaremailtemplate/${webinarId}`, dispatch)
                .then(json => {
                    dispatch(commonActions.startFormEditing("webinarEmail", json, formValidationRules.EmailTemplate))
                })
        }
    },

    sendWebinarEmail(emailForm, callback) {
        return dispatch => {
            dispatch(commonActions.hideAlert());

            const validationResult = helpers.validateForm(emailForm);

            if (validationResult) {
                dispatch(commonActions.showWarning(validationResult, 'Please validate!'));
                return null;
            }

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(emailForm.data)
            }

            dispatch(commonActions.progressLoader(true))

            return httpFetch(`/api/customer/sendWebinarEmail`, dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.showSuccess('Email has been sent'));

                    callback();
                })
        }
    },

    getCompensationsForCustomer(customerNumber) {
        return (dispatch) => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "customerCompensations", value: true });
            return dispatch(
                compensationActions.getCompensationTickets({ customerNumber }, actionTypes.GET_COMPENSATIONS_FOR_CUSTOMER)
            ).finally(() => {
                dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "customerCompensations", value: false });
            });
        }
    },

    updateCustomerCompensationHistoryCount(number) {
        return {
            type: actionTypes.UPDATE_CUSTOMER_COMPENSATION_HISTORY_COUNT,
            compensationHistoryCount: number
        }
    },

    getCampaignsForCustomer(customerNumber) {
        return (dispatch) => {
            dispatch(commonActions.progressLoader(true));
            return httpFetch(`/api/campaign/GetCampaignsForCustomer/${customerNumber}`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_CAMPAIGNS_FOR_CUSTOMER, data: json });
                })
        }
    },

    sendCampaignEmail(emailId, contactId, customerNumber) {
        return (dispatch) => {

            var data = {
                emailId: emailId,
                contactId: contactId
            }

            const httpRequest = {
                method: 'POST',
                body: JSON.stringify(data)
            }

            return httpFetch(`/api/campaign/resendemail`, dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.showSuccess('Email has been sent'));
                    dispatch(customerActions.getCampaignsForCustomer(customerNumber));
                })
        }
    },

    clearCampaignsForCustomer() {
        return {
            type: actionTypes.STORE_CAMPAIGNS_FOR_CUSTOMER,
            data: null
        };
    },

    getHubSpotEmailsForCustomer(customerNumber) {
        return (dispatch) => {
            dispatch(commonActions.progressLoader(true));
            return httpFetch(`/api/customer/${customerNumber}/hubspot-emails`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_HUBSPOT_EMAILS_FOR_CUSTOMER, data: json });
                })
        }
    },

    clearHubSpotEmailsForCustomer() {
        return {
            type: actionTypes.STORE_HUBSPOT_EMAILS_FOR_CUSTOMER,
            data: null
        };
    },

    getBlikkProducts(customerNumber) {
        return (dispatch) => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "blikkData", value: true });
            return httpFetch(`/api/Blikk/GetProducts/${customerNumber}`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_BLIKK_PRODUCTS, data: json });
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "blikkData", value: false });
                })
        }
    },

    getAOCustomerInfo(customerNumber) {
        return dispatch => {
            return httpFetch(`/api/customer/${customerNumber}/aoinfo`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.GET_AO_CUSTOMER_INFO, data: json });
                })
        }
    },

    startAOCustomerInfoEdit(customerNumber) {
        return dispatch => {

            dispatch(commonActions.progressLoader(true))

            return httpFetch(`/api/customer/${customerNumber}/aoinfo`, dispatch)
                .then(json => {

                    dispatch(commonActions.progressLoader(false))
                    dispatch(commonActions.startFormEditing("updateAOCustomerInfoModal", json, formValidationRules.AoCustomerInfo));
                })
        }
    },

    saveAoCustomerInfo(form, callback) {
        const httpRequest = {
            method: 'POST',
            body: JSON.stringify(form.data)
        }

        return dispatch => {
            httpFetch(`/api/customer/saveAoCustomerInfo/`, dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.showSuccess('AO customer info successfully updated!'));
                    dispatch(customerActions.getAOCustomerInfo(form.data.customerNumber, true));
                    callback()
                })
        }
    },

    updateDirectoryCustomer(form, callback) {
        const httpRequest = {
            method: 'POST',
            body: JSON.stringify(form.data)
        }

        return dispatch => {
            httpFetch(`/api/customer/updateDirectoryCustomer/`, dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.showSuccess('Directory customer info successfully updated!'));
                    callback()
                })
        }
    },

    getSentMovies(customerNumber) {
        return (dispatch) => {
            return httpFetch(`/api/movie/getSentMovies/${customerNumber}`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_SENT_MOVIES, data: json });
                })
        }
    },

    getCustomerConnections(customerNumber) {
        return (dispatch) => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "customerConnections", value: true });
            return httpFetch(`/api/customerconnections/${customerNumber}`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_CUSTOMER_CONNECTIONS, data: json });
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "customerConnections", value: false });
                })
        }
    },

    saveCustomerConnections(form, customerNumber, callback) {
        const httpRequest = {
            method: 'POST',
            body: JSON.stringify(form.data)
        }
        return dispatch => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "saveCustomerConnections", value: true });
            httpFetch(`/api/customerconnections/${customerNumber}`, dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.showSuccess('Connected customers successfully updated!'));
                    dispatch(commonActions.cancelFormEditing(form.id));
                    callback()
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "saveCustomerConnections", value: false });
                    this.getCustomerConnections(customerNumber);
                })
        }
    },

    getOrderLimitationReasons(customerNumber, callback) {
        return (dispatch) => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "orderlimitationreasons", value: true });
            return httpFetch(`/api/customer/${customerNumber}/orderlimitationreasons`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_ORDER_LIMITATION_REASONS, data: json });
                }).finally(() => {
                    if (callback) callback();
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "orderlimitationreasons", value: false });
                })
        }
    },

    getTransactionServices(customerNumber) {
        return (dispatch) => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "transactionServices", value: true });
            return httpFetch(`/api/customer/${customerNumber}/transactionServices`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_TRANSACTION_SERVICES, data: json });
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "transactionServices", value: false });
                })
        }
    },

    getInyettData(organizationNumber) {
        return (dispatch) => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "inyettData", value: true });
            return httpFetch(`/api/customer/${organizationNumber}/inyettData`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_INYETT_DATA, data: json });
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "inyettData", value: false });
                })
        }
    },

    getCustomerInteractions(customerNumber, phoneNumber, organizationNumber) {
        return (dispatch) => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "customerInteractions", value: true });
            const params = new URLSearchParams();
            if (phoneNumber) params.append("phoneNumber", phoneNumber);
            if (organizationNumber) params.append("organizationNumber", organizationNumber);
            return httpFetch(`/api/customer/${customerNumber}/interactions?${params}`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_CUSTOMER_INTERACTIONS, data: json });
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "customerInteractions", value: false });
                })
        }
    },

    getCustomerInfoFlags(customer) {
        return (dispatch) => {

            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "customerInfoFlags", value: true });

            const customerNumber = customer.vbCustomer?.customerNumber || customer.vossCustomer?.customerNumber;
            
            if (!customerNumber) {
                dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "customerInfoFlags", value: false });
                return Promise.resolve();
            }

            const isAoCustomer = helpers.isAoCustomer(customer.vbCustomer) || helpers.isAoCustomer(customer.vossCustomer);

            const customerId = customer.vonCustomer?.id || customer.vossCustomer?.id;

            let queryParams = `isAoCustomer=${isAoCustomer}`;
            if (customerId) {
                queryParams += `&customerId=${customerId}`;
            }

            const httpRequest = {
                method: 'GET'
            }

            return httpFetch(`/api/customer/${customerNumber}/customerInfo?${queryParams}`, dispatch, httpRequest)
                .then(json => {
                    dispatch({ 
                        type: actionTypes.STORE_CUSTOMER_INFO_FLAGS, 
                        data: {
                            flags: json.flags || [],
                            counts: json.counts || null
                        }
                    });
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "customerInfoFlags", value: false });
                })
        }
    },

    save2fa(vonCustomer, callback) {
        return (dispatch) => {
            dispatch(commonActions.hideAlert());
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "save2fa", value: true });

            const httpRequest = {
                method: 'GET',
            }

            return httpFetch(`/api/customer/${vonCustomer.customerNumber}/2fa/${!vonCustomer.useTotp}`, dispatch, httpRequest)
                .then(json => {
                    dispatch(commonActions.showSuccess('2fa saved'));
                    dispatch({ type: actionTypes.STORE_VON_CUSTOMER, data: json })
                    callback()
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "save2fa", value: false });
                })
        }
    },

    getVossCustomerInvoices(customerId, paymentStatus) {
        return dispatch => {
            dispatch({ type: actionTypes.STORE_VOSS_CUSTOMER_INVOICES, data: null });
            httpFetch(`/api/customerinvoice/${customerId}?paymentStatus=${paymentStatus}`, dispatch)
                .then(json => dispatch({ type: actionTypes.STORE_VOSS_CUSTOMER_INVOICES, data: json }));
        };
    },

    getVossCustomerBalance(customerId) {
        return dispatch => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "customerBalance", value: true });
            httpFetch(`/api/customerinvoice/${customerId}/balance`, dispatch)
                .then(json => dispatch({ type: actionTypes.STORE_VOSS_CUSTOMER_BALANCE, data: json })).then(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "customerBalance", value: false });
                });
        };
    },

    getVossCustomerInvoice(customerId, id) {
        return dispatch => {
            dispatch(customerActions.storeVossCustomerInvoice({ loading: true }))
            httpFetch(`/api/customerinvoice/${customerId}/${id}`, dispatch)
                .then(json => {
                    dispatch(customerActions.storeVossCustomerInvoice({ invoice: json }))
                })
                .catch(() => {
                    dispatch(customerActions.storeVossCustomerInvoice(null))
                })
        }
    },

    storeVossCustomerInvoice(invoice) {
        return { type: actionTypes.STORE_VOSS_CUSTOMER_INVOICE, data: invoice }
    },

    getCustomerMigrations(customerNumber, customerId) {
        return (dispatch) => {
            dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "migrations", value: true });
            return httpFetch(`/api/customer/${customerNumber}/migrations${customerId ? `?customerId=${customerId}` : ''}`, dispatch)
                .then(json => {
                    dispatch({ type: actionTypes.STORE_MIGRATIONS, data: json });
                }).finally(() => {
                    dispatch({ type: commonActionTypes.SET_DATA_LOADING, key: "migrations", value: false });
                })
        }
    }
}

export default customerActions;